/**
 * Barèmes des notes par niveau, côté écrans de l'administration.
 *
 * Décision de Daniel (7 octobre 2026) : le PRIMAIRE se note de 0 à 10 au
 * demi-point ; le collège et le lycée restent de 0 à 20 au quart de point.
 * Même règle que le serveur : backend2/utils/baremeNotes.js (le pourquoi, les
 * relevés et les pièges y sont écrits).
 *
 * Les tranches d'appréciation (`config_appreciations`) et les seuils de l'outil
 * décisionnel sont réglés par l'école SUR 20, sans distinction de section :
 * une note du primaire est ramenée sur 20 (× 2) avant d'y être comparée. Les
 * seuils ne sont jamais recopiés en dur dans un écran.
 */

export const BAREME_PRIMAIRE = Object.freeze({ max: 10, pas: 0.5 });
export const BAREME_SECONDAIRE = Object.freeze({ max: 20, pas: 0.25 });

/** L'échelle sur laquelle l'école saisit ses tranches et ses seuils. */
export const ECHELLE_TRANCHES = 20;

/** Vrai pour la section « Primaire » (et pas « Préscolaire »). */
export const estSectionPrimaire = (nomSection) =>
  String(nomSection || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase() === 'primaire';

/** Le barème d'une section, à partir de son nom. */
export const baremeDeSection = (nomSection) =>
  (estSectionPrimaire(nomSection) ? BAREME_PRIMAIRE : BAREME_SECONDAIRE);

const plafond = (bareme) => {
  const max = typeof bareme === 'number' ? bareme : Number(bareme?.max);
  return Number.isFinite(max) && max > 0 ? max : ECHELLE_TRANCHES;
};

/**
 * Une valeur (note ou moyenne) ramenée sur l'échelle des tranches de l'école.
 * Par le RAPPORT (× 1 ou × 2, exacts en virgule flottante), jamais « × 20 puis
 * ÷ barème » : 0,11 × 20 ÷ 20 donnait 0,11000000000000001 (voir le serveur).
 */
export const versEchelleTranches = (valeur, bareme) =>
  Number(valeur) * (ECHELLE_TRANCHES / plafond(bareme));

/** Un seuil réglé sur 20 (tranche, seuil décisionnel), ramené sur le barème. */
export const seuilSurBareme = (seuilSur20, bareme) =>
  Number(seuilSur20) * (plafond(bareme) / ECHELLE_TRANCHES);

/** Vrai si la valeur atteint la moitié du barème (10/20, 5/10). */
export const atteintLaMoyenne = (valeur, bareme) => {
  const v = Number.parseFloat(valeur);
  return Number.isFinite(v) && v >= plafond(bareme) / 2;
};

/** La part du barème atteinte, entre 0 et 1 (pour les couleurs et les barres). */
export const partDuBareme = (valeur, bareme) => {
  const v = Number.parseFloat(valeur);
  if (!Number.isFinite(v)) return 0;
  return v / plafond(bareme);
};

/** « 7,5 » : une valeur écrite à la française, sans zéros inutiles. */
export const noteEnFrancais = (valeur) => {
  const v = Number(valeur);
  if (!Number.isFinite(v)) return String(valeur ?? '');
  return String(Math.round(v * 100) / 100).replace('.', ',');
};

/**
 * Pourquoi une saisie est refusée sur ce barème, ou `null` si elle est valable.
 * Une case vide n'est pas une erreur : elle n'est simplement pas envoyée.
 */
export const motifNoteHorsBareme = (valeur, bareme) => {
  if (valeur === '' || valeur === null || valeur === undefined) return null;
  const v = typeof valeur === 'number' ? valeur : Number.parseFloat(String(valeur).replace(',', '.'));
  if (!Number.isFinite(v)) return 'n\'est pas un nombre';
  if (v < 0) return 'est négative';
  if (v > bareme.max) return `dépasse ${noteEnFrancais(bareme.max)}`;
  const pas = Number(bareme.pas);
  if (Number.isFinite(pas) && pas > 0) {
    const rapport = v / pas;
    if (Math.abs(rapport - Math.round(rapport)) > 1e-9) {
      return pas === 0.5 ? 'n\'est pas au demi-point' : `n'est pas un multiple de ${noteEnFrancais(pas)}`;
    }
  }
  return null;
};
